import { useCallback, useEffect, useRef, useState } from "react";
import {
  getPendingCelebrationRank,
  isCelebrated,
  markAsCelebrated,
  markRankAsDetectedWithOptions,
  type CelebrationSurface,
} from "../lib/celebratedRanks";
import { writePendingJobNotification } from "../lib/jobNotifications";

export type LevelUpCelebrationResult = {
  showCelebration: boolean;
  celebrationRankName: string;
  dismiss: () => void;
};

function debugLevelUp(event: string, detail: Record<string, unknown>) {
  void event;
  void detail;
}

export function useLevelUpCelebration(
  rankName: string | undefined,
  characterId: string | null,
  surface: CelebrationSurface
): LevelUpCelebrationResult {
  const activeRankNameRef = useRef<string | null>(null);
  const prevRankNameRef = useRef<string | undefined>(undefined);
  const prevCharacterIdRef = useRef<string | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationRankName, setCelebrationRankName] = useState("");

  useEffect(() => {
    const showRankUpCelebration = (nextRankName: string) => {
      activeRankNameRef.current = nextRankName;
      if (characterId) {
        markAsCelebrated(characterId, nextRankName, surface);
        writePendingJobNotification({
          kind: "level_up",
          characterId,
          rankName: nextRankName,
        });
      }
      setCelebrationRankName(nextRankName);
      setShowCelebration(true);
      debugLevelUp("show", {
        surface,
        characterId,
        nextRankName,
      });
    };

    if (characterId !== prevCharacterIdRef.current) {
      debugLevelUp("character-changed", {
        surface,
        prevCharacterId: prevCharacterIdRef.current,
        nextCharacterId: characterId,
      });
      activeRankNameRef.current = null;
      prevRankNameRef.current = undefined;
      prevCharacterIdRef.current = characterId;
      setShowCelebration(false);
      setCelebrationRankName("");
    }

    if (!rankName) {
      if (characterId && prevRankNameRef.current === undefined) {
        const pendingRankName = getPendingCelebrationRank(characterId, surface);
        if (pendingRankName) {
          prevRankNameRef.current = pendingRankName;
          debugLevelUp("show-pending-without-rank", {
            surface,
            characterId,
            pendingRankName,
          });
          showRankUpCelebration(pendingRankName);
          return;
        }
      }

      debugLevelUp("skip-no-rank", { surface, characterId, rankName });
      prevRankNameRef.current = rankName;
      return;
    }

    if (!characterId) {
      if (prevRankNameRef.current === undefined) {
        prevRankNameRef.current = rankName;
        debugLevelUp("no-character-initialized", { surface, rankName });
        return;
      }

      if (rankName !== prevRankNameRef.current) {
        const prevRankName = prevRankNameRef.current;
        prevRankNameRef.current = rankName;
        debugLevelUp("rank-changed-without-character", {
          surface,
          prevRankName,
          rankName,
        });
        showRankUpCelebration(rankName);
      } else {
        debugLevelUp("rank-unchanged-without-character", { surface, rankName });
      }
      return;
    }

    const hasPendingCelebration =
      isCelebrated(characterId, rankName) &&
      !isCelebrated(characterId, rankName, "chat") &&
      !isCelebrated(characterId, rankName, "home");

    debugLevelUp("evaluate", {
      surface,
      characterId,
      rankName,
      prevRankName: prevRankNameRef.current,
      hasPendingCelebration,
      isChatCelebrated: isCelebrated(characterId, rankName, "chat"),
      isHomeCelebrated: isCelebrated(characterId, rankName, "home"),
      isSurfaceCelebrated: isCelebrated(characterId, rankName, surface),
    });

    if (prevRankNameRef.current === undefined) {
      prevRankNameRef.current = rankName;
      if (hasPendingCelebration) {
        showRankUpCelebration(rankName);
      }
      return;
    }

    if (rankName !== prevRankNameRef.current) {
      prevRankNameRef.current = rankName;
      markRankAsDetectedWithOptions(characterId, rankName, {
        preserveConfirmedOnWhenSameRank: false,
      });
      debugLevelUp("rank-detected", { surface, characterId, rankName });
      showRankUpCelebration(rankName);
    }
  }, [rankName, characterId, surface]);

  const dismiss = useCallback(() => {
    debugLevelUp("dismiss", {
      surface,
      characterId,
      activeRankName: activeRankNameRef.current,
    });
    activeRankNameRef.current = null;
    setShowCelebration(false);
  }, [characterId, surface]);

  return { showCelebration, celebrationRankName, dismiss };
}
